import React from 'react';
import has from 'lodash/has';
import { Box, FormControl, MenuItem, TextField, Theme, Tooltip, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfRte } from '@qualifio/ui-commons/dist/components/qlf-rte';
import { RewardType } from '@loyalty/shared-common/rewards-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { faBoxArchive } from '@fortawesome/pro-solid-svg-icons/faBoxArchive';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { LanguageTabs } from '../../../shared/language-tabs';
import { SxStyles } from '../../../shared/utils/types';
import { Title } from '../../../shared/layout/Title';
import { UploadableImage } from '../../../shared/image';
import { onQlfRteChange } from '../../../shared/forms/on-qlf-rte-change';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useRewardName } from '../../reward-constants';
import { useRewards } from '../../RewardsContext';

const useStyles = makeStyles<Theme>(({ spacing }) => {
  return {
    formLabel: {
      marginTop: '20px !important',
    },
    root: {},
    description: {
      display: 'flex',
    },
    text: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      padding: spacing(0, 0, 0, 2),
    },
    nameField: {
      marginBottom: spacing(2),
    },
    descriptionField: {
      flex: 1,
    },
    categoryField: {
      width: 'auto',
      minWidth: 200,
      marginRight: 1,
    },
    dropdownLabel: {
      margin: 0,
      marginRight: 0.8,
      marginBottom: 0.8,
    },
    archivedIcon: {
      marginLeft: '20px',
      color: colorPalette.grey.main,
    },
  };
});

const styles: SxStyles = {
  categoryField: {
    width: 'auto',
    minWidth: 200,
    marginRight: 1,
  },
  dropdownLabel: {
    margin: 0,
    marginRight: 0.8,
    marginBottom: 0.8,
  },
};

export function RewardEdition(): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();
  const { program } = useProgram();
  const { features } = useFeatures();
  const { write: rewardWrite } = features[Feature.REWARD];
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    getValues,
  } = useFormContext();
  const { selectedReward } = useRewards();
  if (!selectedReward) {
    throw new Error('No reward selected');
  }

  const programId = program.id;

  const handleUploadError = (name: string, message: string | undefined) => {
    if (message) {
      setError(name, { message });
    } else {
      clearErrors(name);
    }
  };
  const rewardName: Record<string, string> = useRewardName();

  return (
    <div className={classes.root}>
      <Title>
        {t('rewards.edition.title')}
        {selectedReward?.deletedAt ? (
          <Tooltip title={t('rewards.edition.archived')}>
            <FontAwesomeIcon icon={faBoxArchive} className={classes.archivedIcon} />
          </Tooltip>
        ) : (
          ''
        )}
      </Title>

      <LanguageTabs
        languages={program.languages}
        message={t('rewards.edition.message')}
        errors={errors?.i18nAttributes}
      >
        {({ language }) => (
          <div key={language} className={classes.description}>
            <Controller
              control={control}
              name={`i18nAttributes.${language}.image`}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <UploadableImage
                  {...field}
                  disabled={!rewardWrite || !!selectedReward.deletedAt}
                  programId={programId}
                  type="reward"
                  errorMessage={error?.message}
                  onError={handleUploadError}
                />
              )}
            />
            <div className={classes.text}>
              <Controller
                control={control}
                name={`i18nAttributes.${language}.name`}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    disabled={!rewardWrite || !!selectedReward.deletedAt}
                    placeholder={t('rewards.edition.placeholders.name')}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ maxLength: 200 }}
                    id="reward-create-name-id"
                  />
                )}
              />

              <Controller
                control={control}
                name={`i18nAttributes.${language}.description`}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <QlfRte
                    {...field}
                    onChange={onQlfRteChange(
                      field.onChange,
                      () => getValues().i18nAttributes?.[language]?.description,
                    )}
                    disabled={!rewardWrite || !!selectedReward.deletedAt}
                    label={t('rewards.edition.placeholders.description')}
                    error={!!error}
                    helperText={error?.message}
                    rows={5}
                    multiline
                  />
                )}
              />

              <Typography variant="body1" className={classes.formLabel}>
                {t('rewards.edition.title.details-section')}
              </Typography>

              <Controller
                control={control}
                name={`i18nAttributes.${language}.detailsLabel`}
                defaultValue={t('rewards.details.more.default-label')}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    disabled={!rewardWrite || !!selectedReward.deletedAt}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ maxLength: 100 }}
                    id="reward-create-name-id"
                    sx={theme => ({
                      marginBottom: theme.spacing(1),
                    })}
                  />
                )}
              />

              <Controller
                control={control}
                name={`i18nAttributes.${language}.details`}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <QlfRte
                    {...field}
                    onChange={onQlfRteChange(
                      field.onChange,
                      () => getValues().i18nAttributes?.[language]?.details,
                    )}
                    disabled={!rewardWrite || !!selectedReward.deletedAt}
                    error={!!error}
                    helperText={error?.message}
                    // FIXME: uncomment once on ui-commons 13.x
                    label={t('rewards.details.more.details.placeholder')}
                    rows={5}
                    multiline
                  />
                )}
              />
            </div>
          </div>
        )}
      </LanguageTabs>

      <Box component="div" sx={{ display: 'flex', alignItems: 'baseline' }}>
        <Typography variant="h3" sx={styles.dropdownLabel}>
          {t('rewards.type')}
        </Typography>
        <Controller
          control={control}
          name="type"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <FormControl sx={styles.categoryField}>
              <TextField
                select
                error={has(errors, 'type')}
                helperText={error?.message}
                {...field}
                value={field.value ?? ''}
                inputRef={ref}
                disabled={!!selectedReward.createdAt}
                sx={{ paddingTop: 1 }}
              >
                {Object.keys(RewardType).map(value => (
                  <MenuItem key={value} value={value} id={`reward_type_${value}`}>
                    {rewardName[value]}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          )}
        />
      </Box>
    </div>
  );
}
